import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ModificarEmpleadoFacade } from '../services/modificar-empleado.facade';
import { DatosPersonaEmpleadoComponent } from '../components/datos-persona/datos-persona.component';
import { InformacionAdicionalEmpleadoComponent } from '../components/informacion-adicional/informacion-adicional.component';
import { MovilidadConsultorComponent } from '../components/movilidad-consultor/movilidad-consultor.component';
import { DatosConsultorEmpleadoComponent } from '../components/datos-consultor/datos-consultor.component';
import { PersonaDialogComponent } from 'src/app/presentation/personas/persona-dialog.component';
import { MunicipioSeleccionado } from 'src/app/presentation/common/busquedas/busqueda-municipio-dialog.component';
import { AsociacionesEmpleadoDialogComponent } from 'src/app/presentation/empleados/shared/asociacion-catalogo/asociaciones-empleado-dialog.component';

/**
 * Contenido de "Modificar Empleado/Consultor" (HU1259115), embebido en un
 * `p-dialog` desde `AdministrarEmpleadosComponent` (fase 19) — mismo patrón
 * de modal-edición que `ModificarEncabezadoFacturaComponent` en Servicios
 * Entregados: recibe el id por `@Input()`, no lee la ruta ni navega, y
 * emite `guardado`/`cancelado` para que el padre cierre el diálogo y
 * refresque el listado.
 *
 * <p>Legacy: `ModificarEmpleadoViewImpl` (~2000 líneas, 4 secciones: Datos de
 * Persona, Información Adicional, Información Movilidad Consultor, Datos
 * Consultor). Migra el análisis exhaustivo del legado (fase 21 de
 * Administrar-Empleados-Asesores-seguimiento.md) más la corrección de
 * fidelidad estructural (frontend-girop §14, F23 del plan de "Modificar
 * Empleado"): el formulario se dividió en 4 dumb components +
 * este container, siguiendo el orden y agrupación real del `.ui.xml`
 * (hallazgos A5/A6/A8 de la auditoría) en vez de la agrupación aproximada
 * usada antes de esta fase.</p>
 *
 * <p>Simplificación heredada (Escenario 2 de HU1259115, ya presente antes de
 * esta fase): la sección "Datos Consultor" (y Movilidad Consultor) y sus
 * reglas condicionales solo se activan para `cdtipoEmpleado === Asesor`
 * exacto, no para el grupo más amplio Asesor/DirTecnico/AsesorLineaAt del
 * legado — ver plan de implementación, fase de ampliación a
 * {A,DT,AL} (pendiente, requiere nuevas constantes de tipo de empleado).</p>
 *
 * <p>Sin lógica de negocio propia (frontend-girop §14): todo vive en
 * {@link ModificarEmpleadoFacade}; este container solo orquesta template y
 * delega los eventos de los 4 dumb components a los métodos del facade.</p>
 */
@Component({
  selector: 'app-modificar-empleado',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ProgressSpinnerModule,
    DatosPersonaEmpleadoComponent,
    InformacionAdicionalEmpleadoComponent,
    MovilidadConsultorComponent,
    DatosConsultorEmpleadoComponent,
    PersonaDialogComponent,
    AsociacionesEmpleadoDialogComponent,
  ],
  providers: [ModificarEmpleadoFacade],
  template: `
    <div class="p-3 sm:p-4 overflow-x-hidden">
      @if (facade.cargando()) {
        <div class="flex justify-center p-8">
          <p-progressSpinner strokeWidth="4" [style]="{ width: '50px', height: '50px' }"></p-progressSpinner>
        </div>
      }

      @if (facade.errorCarga()) {
        <div class="p-3 mb-4 rounded bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200">
          {{ facade.errorCarga() }}
        </div>
      }

      @if (!facade.cargando() && !facade.errorCarga() && facade.detalle(); as detalle) {
        @if (facade.mensaje()) {
          <div class="p-3 mb-4 rounded whitespace-pre-line"
            [ngClass]="{
              'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200': facade.mensajeTipo() === 'error',
              'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200': facade.mensajeTipo() === 'info'
            }">
            {{ facade.mensaje() }}
          </div>
        }

        <app-datos-persona-empleado
          [detalle]="detalle"
          [form]="facade.form"
          [esAsesor]="facade.esAsesor()"
          [mostrarProgramable]="facade.mostrarProgramable()"
          [oficinas]="facade.oficinas()"
          [tiposEmpleado]="facade.tiposEmpleado()"
          [perfiles]="facade.perfiles()"
          [tiposAsesorReal]="facade.tiposAsesorReal()"
          [tiposFacturacion]="facade.tiposFacturacion()"
          [proyectos]="facade.proyectos()"
          [soluciones]="facade.soluciones()"
          [municipioResidenciaDisplay]="facade.municipioResidenciaDisplay()"
          [municipioPrestacionDisplay]="facade.municipioPrestacionDisplay()"
          [cargandoPersonaDetalle]="facade.cargandoPersonaDetalle()"
          [fotoUrl]="facade.fotoUrl()"
          [cargandoFoto]="facade.cargandoFoto()"
          [errorFoto]="facade.errorFoto()"
          (cambioTipoEmpleado)="facade.onCambioTipoEmpleado()"
          (cambioTipoFacturacion)="facade.onCambioTipoFacturacion()"
          (municipioResidenciaSeleccionado)="onMunicipioResidenciaSeleccionado($event)"
          (municipioResidenciaLimpiado)="facade.limpiarMunicipioResidencia()"
          (municipioPrestacionSeleccionado)="onMunicipioPrestacionSeleccionado($event)"
          (municipioPrestacionLimpiado)="facade.limpiarMunicipioPrestacion()"
          (verDetallePersona)="onVerDetallePersona()"
          (fotoSeleccionada)="facade.seleccionarFoto($event)">
        </app-datos-persona-empleado>

        <app-informacion-adicional-empleado
          [detalle]="detalle"
          [salarioDisplay]="facade.salarioDisplay()">
        </app-informacion-adicional-empleado>

        @if (facade.esAsesor()) {
          <app-movilidad-consultor
            [form]="facade.form"
            [opcionesTipoAsignacion]="facade.opcionesTipoAsignacion">
          </app-movilidad-consultor>

          <app-datos-consultor-empleado
            [form]="facade.form"
            [gruposConocimiento]="facade.gruposConocimiento()"
            (cambioAdscrito)="facade.onCambioAdscrito()"
            (cambioEspecializacionHigiene)="facade.onCambioEspecializacionHigiene()">
          </app-datos-consultor-empleado>
        }

        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <p-button label="Asociaciones" icon="pi pi-sitemap" [outlined]="true"
            styleClass="min-h-[44px]" (onClick)="mostrarAsociaciones.set(true)"></p-button>
          <p-button label="Cancelar" icon="pi pi-times" [outlined]="true"
            styleClass="min-h-[44px]" (onClick)="onCancelar()"></p-button>
          <p-button label="Grabar" icon="pi pi-check" styleClass="min-h-[44px]"
            [loading]="facade.guardando()" (onClick)="grabar(detalle.nmconsecutivo)"></p-button>
        </div>

        @if (mostrarAsociaciones()) {
          <app-asociaciones-empleado-dialog
            [visible]="mostrarAsociaciones()"
            (visibleChange)="mostrarAsociaciones.set($event)"
            [nmconsecutivoEmpleado]="detalle.nmconsecutivo">
          </app-asociaciones-empleado-dialog>
        }
      }
    </div>

    @if (mostrarDialogoDetallePersona()) {
      <app-persona-dialog
        [visible]="mostrarDialogoDetallePersona()"
        (visibleChange)="mostrarDialogoDetallePersona.set($event)"
        [editMode]="true"
        [soloLectura]="true"
        [persona]="facade.personaDetalle()"
        [tiposEmpresa]="facade.tiposEmpresaPersona()"
        [tamaniosEmpresa]="facade.tamaniosEmpresaPersona()">
      </app-persona-dialog>
    }
  `
})
export class ModificarEmpleadoComponent implements OnChanges {
  protected readonly facade = inject(ModificarEmpleadoFacade);

  /** Id del empleado a modificar. Al cambiar (diálogo recién abierto con otro registro), recarga el detalle. */
  @Input() nmconsecutivo: number | null = null;

  /** Se emite tras guardar con éxito — el padre cierra el diálogo y refresca el listado. */
  @Output() guardado = new EventEmitter<void>();

  /** Se emite al cancelar — el padre solo cierra el diálogo. */
  @Output() cancelado = new EventEmitter<void>();

  /** Diálogo de solo lectura de "Ver Detalle Persona" (legacy: `activity.verDetallePersona()`). */
  protected readonly mostrarDialogoDetallePersona = signal(false);

  /** Diálogo "Asociaciones" (HU1259149, Fase 3) — sin equivalente directo en el legado
   *  (que navega a 5 Places separados desde `ModificarEmpleadoActivity.goTo*()`). */
  protected readonly mostrarAsociaciones = signal(false);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['nmconsecutivo'] && this.nmconsecutivo != null) {
      this.facade.cargar(this.nmconsecutivo);
    }
  }

  onMunicipioResidenciaSeleccionado(municipio: MunicipioSeleccionado): void {
    this.facade.seleccionarMunicipioResidencia(municipio.cdmunicipio, municipio.dsmunicipio);
  }

  onMunicipioPrestacionSeleccionado(municipio: MunicipioSeleccionado): void {
    this.facade.seleccionarMunicipioPrestacion(municipio.cdmunicipio, municipio.dsmunicipio);
  }

  /**
   * Legacy: `activity.verDetallePersona()` — carga el detalle completo de
   * la persona (fetch aparte, ver Javadoc de
   * `ModificarEmpleadoFacade.cargarDetallePersona()`) y solo entonces abre
   * el diálogo, para no mostrarlo vacío mientras carga.
   */
  async onVerDetallePersona(): Promise<void> {
    await this.facade.cargarDetallePersona();
    this.mostrarDialogoDetallePersona.set(true);
  }

  async grabar(nmconsecutivo: number): Promise<void> {
    const exito = await this.facade.guardar(nmconsecutivo);
    if (exito) {
      this.guardado.emit();
    }
  }

  onCancelar(): void {
    this.cancelado.emit();
  }
}
